import React, { useMemo } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  collectSectionSubtreeIds,
  selectableReportSections,
  selectedReportSectionIds,
  toggleReportSectionRef,
  type SectionOutlineLike,
} from './reportSectionSources';

type ReportSectionSourcesPickerProps = {
  /** Every section of the report (flat). */
  sections: SectionOutlineLike[];
  /** The section being edited; it and its descendants are not offered. */
  currentSectionId: string | null | undefined;
  /**
   * The section's full data_sources array; only 'Report Section' entries are
   * touched.
   */
  value: unknown;
  onChange: (next: unknown[]) => void;
  disabled?: boolean;
};

type OutlineNode = SectionOutlineLike & { children: OutlineNode[] };

function buildOutline(sections: SectionOutlineLike[]): OutlineNode[] {
  const ordered = [...sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const byId = new Map(ordered.map((s) => [s.id, s]));
  const childrenByParent = new Map<string, SectionOutlineLike[]>();
  for (const section of ordered) {
    const parentId = section.parent_section_id;
    if (!parentId || !byId.has(parentId)) continue;
    const list = childrenByParent.get(parentId) ?? [];
    list.push(section);
    childrenByParent.set(parentId, list);
  }
  const build = (
    section: SectionOutlineLike,
    seen: Set<string>
  ): OutlineNode => ({
    ...section,
    children: (childrenByParent.get(section.id) ?? [])
      .filter((child) => !seen.has(child.id))
      .map((child) => build(child, new Set([...seen, child.id]))),
  });
  return ordered
    .filter((s) => !s.parent_section_id || !byId.has(s.parent_section_id))
    .map((s) => build(s, new Set([s.id])));
}

/**
 * Checklist of the report's other sections. A parent with children is a
 * "category": checking it references it and every subsection; its children can
 * also be picked one by one.
 */
export default function ReportSectionSourcesPicker({
  sections,
  currentSectionId,
  value,
  onChange,
  disabled = false,
}: ReportSectionSourcesPickerProps) {
  const selectable = useMemo(
    () => selectableReportSections(sections, currentSectionId),
    [sections, currentSectionId]
  );
  const outline = useMemo(() => buildOutline(selectable), [selectable]);
  const selectedIds = useMemo(() => selectedReportSectionIds(value), [value]);
  const coveredByParent = useMemo(() => {
    const covered = new Set<string>();
    if (!Array.isArray(value)) return covered;
    for (const ds of value) {
      const rec = ds as {
        source?: string;
        id?: string;
        includeChildren?: boolean;
      };
      if (rec?.source !== 'Report Section' || !rec.id || !rec.includeChildren)
        continue;
      for (const id of collectSectionSubtreeIds(selectable, rec.id)) {
        if (id !== rec.id) covered.add(id);
      }
    }
    return covered;
  }, [value, selectable]);

  if (selectable.length === 0) {
    return (
      <p className="tw:text-sm tw:text-muted-foreground">
        No other sections in this report yet.
      </p>
    );
  }

  const toggle = (section: SectionOutlineLike, includeChildren: boolean) =>
    onChange(
      toggleReportSectionRef({
        dataSources: value,
        section,
        includeChildren,
        sections: selectable,
      })
    );

  const renderNode = (node: OutlineNode, depth: number): React.ReactNode => {
    const isCategory = node.children.length > 0;
    const checked = selectedIds.has(node.id);
    const covered = !checked && coveredByParent.has(node.id);
    const inputId = `report-section-source-${node.id}`;
    return (
      <li key={node.id} className="tw:list-none">
        <div
          className="tw:flex tw:min-w-0 tw:items-start tw:gap-2 tw:px-0.5 tw:py-1.5 tw:ps-0"
          style={{ paddingLeft: depth * 12 }}
        >
          <Checkbox
            id={inputId}
            className="tw:mt-0.5"
            checked={checked || covered}
            indeterminate={covered}
            disabled={disabled || covered}
            onCheckedChange={() => toggle(node, isCategory)}
          />
          <Label
            htmlFor={inputId}
            className="tw:block tw:min-w-0 tw:flex-1 tw:font-normal tw:leading-5 tw:whitespace-normal tw:break-words"
          >
            {node.name}
            {isCategory ? (
              <span className="tw:ms-1 tw:text-xs tw:text-muted-foreground">
                (category · includes {node.children.length} subsection
                {node.children.length === 1 ? '' : 's'})
              </span>
            ) : null}
          </Label>
        </div>
        {isCategory ? (
          <ul className="tw:m-0 tw:list-none tw:p-0 tw:ps-0!">
            {node.children.map((child) => renderNode(child, depth + 1))}
          </ul>
        ) : null}
      </li>
    );
  };

  return (
    <ScrollArea
      className="tw:h-64 tw:min-w-0 tw:rounded-md tw:border tw:border-border"
      viewportClassName="tw:py-1 tw:pl-2.5 tw:pr-5"
      data-testid="report-section-sources-picker"
    >
      <ul className="tw:m-0 tw:min-w-0 tw:list-none tw:p-0 tw:ps-0!">
        {outline.map((node) => renderNode(node, 0))}
      </ul>
    </ScrollArea>
  );
}
